import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import ShoppingCart from 'lucide-react/dist/esm/icons/shopping-cart.js';
import Check from 'lucide-react/dist/esm/icons/check.js';
import ChevronDown from 'lucide-react/dist/esm/icons/chevron-down.js';

const springTransition = { type: 'spring', stiffness: 520, damping: 24 };

function transitionFor(reduceMotion) {
  return reduceMotion ? { duration: 0 } : springTransition;
}

/**
 * A deliberately small morphicon for actions that move from intent to
 * confirmation. Keep it scoped to state-changing controls; static navigation
 * icons should remain static.
 */
export function MorphingCartIcon({ confirmed = false, label }) {
  const reduceMotion = useReducedMotion();

  return (
    <span className="morphing-cart-icon" aria-hidden="true">
      <AnimatePresence initial={false} mode="popLayout">
        {confirmed ? (
          <motion.span
            key="confirmed"
            className="morphing-cart-icon__glyph"
            initial={{ opacity: 0, scale: 0.65, rotate: -18 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.7, rotate: 18 }}
            transition={transitionFor(reduceMotion)}
          >
            <Check className="h-5 w-5" strokeWidth={2.8} />
          </motion.span>
        ) : (
          <motion.span
            key="cart"
            className="morphing-cart-icon__glyph"
            initial={{ opacity: 0, scale: 0.7, rotate: 12 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.7, rotate: -12 }}
            transition={transitionFor(reduceMotion)}
          >
            <ShoppingCart className="h-5 w-5" />
          </motion.span>
        )}
      </AnimatePresence>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  );
}

export function MorphingMenuIcon({ open = false }) {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion
    ? { duration: 0 }
    : { type: 'spring', stiffness: 480, damping: 30 };

  return (
    <span className="morphing-menu-icon" aria-hidden="true">
      <motion.span
        className="morphing-menu-icon__line"
        animate={{ y: open ? 7 : 0, rotate: open ? 45 : 0 }}
        transition={transition}
      />
      <motion.span
        className="morphing-menu-icon__line"
        animate={{ opacity: open ? 0 : 1, scaleX: open ? 0.35 : 1 }}
        transition={transition}
      />
      <motion.span
        className="morphing-menu-icon__line"
        animate={{ y: open ? -7 : 0, rotate: open ? -45 : 0 }}
        transition={transition}
      />
    </span>
  );
}

export function MorphingPlusMinusIcon({ expanded = false }) {
  const reduceMotion = useReducedMotion();
  const transition = reduceMotion ? { duration: 0 } : { duration: 0.18, ease: 'easeOut' };

  return (
    <span className="morphing-plus-minus-icon" aria-hidden="true">
      <span className="morphing-plus-minus-icon__line" />
      <motion.span
        className="morphing-plus-minus-icon__line"
        style={{ rotate: 90 }}
        animate={{ scaleY: expanded ? 0 : 1 }}
        transition={transition}
      />
    </span>
  );
}

export function MorphingChevronIcon({ expanded = false }) {
  const reduceMotion = useReducedMotion();

  return (
    <motion.span
      className="morphing-chevron-icon"
      animate={{ rotate: expanded ? 180 : 0 }}
      transition={reduceMotion ? { duration: 0 } : { duration: 0.2, ease: 'easeOut' }}
      aria-hidden="true"
    >
      <ChevronDown className="h-5 w-5" />
    </motion.span>
  );
}
